/**
 * useFirmNameSuggestions — ranked register suggestions for a Firmenname input.
 *
 * One request per 3-character stem (firmNameStem), debounced 200 ms, cached 5 minutes.
 * Everything typed after the stem is ranked locally (rankFirmNames) against the full list
 * the api returned, so keystrokes past the stem never hit the network. The list is only
 * ranked when it belongs to the CURRENT stem — while a new stem is in flight the result is
 * empty rather than a stale list from the previous stem.
 *
 * [Source: apps/web/src/features/search/hooks/useSearch.ts] (debounce + useQuery shape)
 * [Source: packages/shared/src/firm-name-match.ts] (stem + ranking)
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { firmNameStem, prepareFirmNames, rankFirmNames } from '@brokly/shared';
import type { FirmNameSuggestion, FirmNameSuggestResponse } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { FIRM_NAME_CACHE_MS, FIRM_NAME_DEBOUNCE_MS } from '../constants';

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface FirmNameSuggestionsState {
  /** Ranked for the full input; ALL matches, the caller decides how many to show. */
  suggestions: FirmNameSuggestion[];
  isLoading: boolean;
  isError: boolean;
}

export function useFirmNameSuggestions(input: string): FirmNameSuggestionsState {
  const stem = firmNameStem(input);
  const debouncedStem = useDebounced(stem, FIRM_NAME_DEBOUNCE_MS);

  const query = useQuery<FirmNameSuggestResponse>({
    queryKey: qk.firmNameSuggestions(debouncedStem ?? ''),
    queryFn: ({ signal }) => apiClient.register.suggestFirmNames(debouncedStem as string, signal),
    enabled: debouncedStem !== null,
    staleTime: FIRM_NAME_CACHE_MS,
    gcTime: FIRM_NAME_CACHE_MS,
    retry: false,
  });

  const data = query.data;
  const prepared = useMemo(() => (data ? prepareFirmNames(data.suggestions) : []), [data]);
  const suggestions = useMemo(
    () => (data && stem !== null && data.stem === stem ? rankFirmNames(prepared, input) : []),
    [data, prepared, stem, input],
  );

  return {
    suggestions,
    isLoading: stem !== null && (query.isPending || debouncedStem !== stem),
    isError: query.isError,
  };
}
